import { useMemo, useState } from 'react';
import { Stack, Group, Text, Badge, Button, ActionIcon, Menu, Loader, Center, Table } from '@mantine/core';
import { IconEdit, IconTrash, IconDots, IconFilter } from '@tabler/icons-react';
import { ResumeContent, useDeleteResumeContent } from '../../api/resumes';
import { useResumeStore, ResumeContentType } from '../../store/resume.store';

interface ResumeContentLibraryProps {
  contentItems: ResumeContent[];
  isLoading: boolean;
  onEdit: (id: string) => void;
}

// Helper function to format content type for display
function formatContentType(type: ResumeContentType): string {
  const typeMap: Record<ResumeContentType, string> = {
    masterTemplate: 'Master Template',
    skills: 'Skills',
    experience: 'Experience',
    fullResume: 'Full Resume',
    education: 'Education',
    notes: 'Notes',
  };
  return typeMap[type];
}

export default function ResumeContentLibrary({ contentItems, isLoading, onEdit }: ResumeContentLibraryProps) {
  const { contentTypeFilter, setContentTypeFilter } = useResumeStore();

  const filteredContent = useMemo(() => {
    if (!contentTypeFilter) return contentItems;
    return contentItems.filter((item) => item.type === contentTypeFilter);
  }, [contentItems, contentTypeFilter]);

  const typeOptions: { label: string; value: ResumeContentType | undefined }[] = [
    { label: 'All Types', value: undefined },
    { label: 'Master Template', value: 'masterTemplate' },
    { label: 'Skills', value: 'skills' },
    { label: 'Experience', value: 'experience' },
    { label: 'Full Resume', value: 'fullResume' },
    { label: 'Education', value: 'education' },
    { label: 'Notes', value: 'notes' },
  ];

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (filteredContent.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Text c="dimmed">No resume content yet</Text>
          <Text size="sm" c="dimmed">
            Create your first content item to get started
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      {/* Filter */}
      <Group>
        <IconFilter size={16} />
        <Group gap="xs">
          {typeOptions.map((option) => (
            <Badge
              key={option.value || 'all'}
              variant={contentTypeFilter === option.value ? 'filled' : 'light'}
              style={{ cursor: 'pointer' }}
              onClick={() => setContentTypeFilter(option.value)}
            >
              {option.label}
            </Badge>
          ))}
        </Group>
      </Group>

      {/* Content Items Table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredContent.map((item) => (
            <ContentItemRow key={item.id} item={item} onEdit={onEdit} />
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

interface ContentItemRowProps {
  item: ResumeContent;
  onEdit: (id: string) => void;
}

function ContentItemRow({ item, onEdit }: ContentItemRowProps) {
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const deleteContent = useDeleteResumeContent(item.id);

  const handleDelete = async () => {
    try {
      await deleteContent.mutateAsync();
      setDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete content:', error);
    }
  };

  const createdDate = new Date(item.created_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const statusBadges = (
    <Group gap="xs">
      {item.type === 'masterTemplate' && item.is_default && (
        <Badge size="sm" color="blue">
          Default
        </Badge>
      )}
      {item.type !== 'masterTemplate' && item.include_by_default && (
        <Badge size="sm" color="green">
          Include by Default
        </Badge>
      )}
    </Group>
  );

  if (deleteConfirm) {
    return (
      <Table.Tr>
        <Table.Td colSpan={6}>
          <Group gap="xs">
            <Text size="sm" c="red">
              Are you sure? This cannot be undone.
            </Text>
            <Group gap="xs" ml="auto">
              <Button
                size="xs"
                variant="light"
                onClick={() => setDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="red"
                loading={deleteContent.isPending}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Group>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Table.Tr>
      <Table.Td>
        <Text
          fw={500}
          onClick={() => onEdit(item.id)}
          style={{ cursor: 'pointer' }}
        >
          {item.name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge size="sm" variant="light">
          {formatContentType(item.type)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {item.description || '—'}
        </Text>
      </Table.Td>
      <Table.Td>{statusBadges}</Table.Td>
      <Table.Td>
        <Text size="sm">{createdDate}</Text>
      </Table.Td>
      <Table.Td>
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={() => onEdit(item.id)}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTrash size={14} />}
              color="red"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  );
}
